// eslint-disable-next-line max-classes-per-file
const ajax = require('component-ajax');
const q = require('queried');
const ext = require('deep-extend');
const domify = require('domify');
const scrollTo = require('scroll-to');
const getOffsets = require('mucss/offset');
const on = require('emmy/on');
const off = require('emmy/off');
const emit = require('emmy/emit');
const css = require('mucss/css');
const echoFactory = require('echo-js');
const { sendCountersView } = require('../external-counters');
const AdvertButton = require('../advert-button');
const {
  sortArrayByField,
} = require('../utils');

const echo = echoFactory(window);
echo.detach();
echo.init({
  offset: 100,
  throttle: 250,
  unload: false,
  callback(elem, op) {
    elem.setAttribute('data-echo-loaded', op == 'load');
  },
});

// eslint-disable-next-line import/order
const Swiper = require('swiper');
const _ = require('../i18n');
const {
  Controller,
  View,
  createUrl,
} = require('./core');
const SpecialWidget = require('../special-widgets-loader');

function createSwiper(elem, imageUrls) {
  const swiper = new Swiper(elem, {
    loop: true,
    effect: 'fade',
    speed: 200,
    autoplay: {
      delay: 1000,
    },
    lazyLoading: true,
    preloadImages: false,
  });

  swiper.autoplay.stop();

  const slides = imageUrls.map((url) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const imgEl = document.createElement('div');
    imgEl.setAttribute('data-src', url);
    imgEl.classList.add('post-image-src');
    css(imgEl, 'background-image', `url(${url})`);

    slide.appendChild(imgEl);

    return slide;
  });

  if (slides.length) {
    swiper.appendSlide(slides);
  }

  return swiper;
}

class FeedController extends Controller {
  constructor(...args) {
    super(...args);

    const relatedFeedElements = q.all('.feed--related .post', this.view.element);
    const feedElements = q.all('.eotd, .post, .special-widget', this.view.element)
      .filter((item) => !relatedFeedElements.includes(item));
    const initialState = {
      feed: feedElements.map((e) => e.outerHTML),
      relatedFeed: relatedFeedElements.map((e) => e.outerHTML),
    };

    this.model.update((state) => ext(state, initialState));
  }

  enable(key, value, keyInUrlPath = false) {
    this.model.updateWithSync((state) => ext(state, {
      page: {
        urlFacetKey: keyInUrlPath ? key : state.page.urlFacetKey,
        index: 1,
      },
      facetFilter: {
        [key]: [value],
      },
    }));
  }

  postProcessData(data) {
    let { feed } = data;

    if (this.model && this.model.requestParams.from_item) {
      // feed-loading may be called from both: mix & item
      // but when we call "endless" feed for item - we must filter it from item itself
      //
      // if item was already droped on first load - we may check it from model.itemDroped
      // and in this case - we dont need process loaded feed further
      if (this.model.itemDroped) { return feed.slice(1, 31); }
      // but, if item didnt loaded before - we must filter feed from it here
      // also, thats why server answer with 31 items (if we send request with "from_item" param)
      // so we must slice answer to 30 items anyway
      feed = feed.filter(
        (feedItem) => !feedItem.includes(window._metricsConfig.item.slug),
      );
      if (feed.length != data.feed.length) { this.model.itemDroped = true; }

      return feed.slice(0, 30);
    }

    return feed;
  }

  sync(data, state, prevState) {
    const feed = this.postProcessData(data);
    const isLoadMore = !prevState.page.loadMoreInProgress && state.page.loadMoreInProgress;

    if (state.feed && isLoadMore) {
      state.feed = [].concat(state.feed, feed);
    } else {
      state.feed = feed;
    }

    if (data.related_feed) {
      state.relatedFeed = data.related_feed;
    }

    return state;
  }
}

class FeedLayout extends View {
  constructor(...args) {
    super(...args);

    this.AdButton = new AdvertButton();

    this.events.bind('click a[data-key]', 'enableKey');

    this.events.bind('click .post-detail-address-link', 'scrollToMap');

    this.preloader = domify(`
      <div class="feed-preload-wrap">
        <div class="kudago-preloader"></div>
      </div>
    `);
  }

  scrollToMap() {
    setImmediate(() => {
      const map = q('.map-view-preview');
      if (map) {
        const { top } = getOffsets(map);
        const freeSpace = (window.innerHeight - 540) / 2;
        const offset = top - Math.max(freeSpace, 0);
        scrollTo(window.pageXOffset, offset, { duration: 300 });
      }
    });
  }

  scrollToFeed() {
    const feed = q('.feed');
    if (feed) {
      const { top } = getOffsets(feed);
      if (top < getScroll()) {
        scrollTo(window.pageXOffset, top - 80, { duration: 300 });
      }
    }

    function getScroll() {
      // see http://stackoverflow.com/questions/3464876/javascript-get-window-x-y-position-for-scroll
      const doc = document.documentElement;
      return (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    }
  }

  /**
   * Reset previously inited swipers,
   * then bind mouse enter/leave listeners
   * for preview containers and map link.
   *
   * We use 'mouseenter' and 'mouseleave'
   * to ensure events will fire for
   * preview and address elements only.
   */
  initSwipers() {
    const oldSwipers = this.swipers || {};
    Object
      .keys(oldSwipers)
      .map((key) => oldSwipers[key])
      .forEach((swiper) => swiper.destroy(false, false));

    this.swipers = {};

    const previews = q.all('.post-preview', this.element);
    previews.forEach((previewElem) => {
      const swiperElem = q('.swiper-container', previewElem);
      if (swiperElem) {
        const imagesUrl = swiperElem.getAttribute('data-images');
        this.bindPreviewEvents(previewElem, swiperElem, imagesUrl);
        this.bindMapEvents(previewElem, imagesUrl);
      }
    });
  }

  /**
   * Fetches swiper images if need.
   * Creates swiper instance for previewElem if need.
   * Appends map image.
   *
   * @param previewElem
   * @param swiperElem
   * @param imagesUrl
   */
  bindPreviewEvents(previewElem, swiperElem, imagesUrl) {
    let hoverPreview = false;

    on(previewElem, 'mouseenter', () => {
      hoverPreview = true;

      const swiper = this.swipers[imagesUrl];
      if (swiper) {
        if (hoverPreview) {
          swiper.autoplay.start();
        }
      } else {
        this.fetchImages(imagesUrl, (serverImages) => {
          if (serverImages.length) {
            const map = swiperElem.getAttribute('data-map');

            const images = serverImages.filter((url) => !q(`[data-src="${url}"]`, swiperElem));

            if (map) {
              images.push(map);
            }

            this.swipers[imagesUrl] = createSwiper(swiperElem, images);
            if (hoverPreview) {
              this.swipers[imagesUrl].autoplay.start();
            }
          }
        });
      }
    });

    on(previewElem, 'mouseleave', () => {
      hoverPreview = false;

      const swiper = this.swipers[imagesUrl];
      if (swiper) {
        swiper.slideTo(1);
        swiper.autoplay.stop();
      }
    });
  }

  setPingIndices() {
    const items = q.all('.post-rect, .post-premiere', this.element);

    items.forEach((item, i) => {
      if (item.hasAttribute('data-ping-view')) {
        item.setAttribute('data-ping-position', i + 1);
      }
    });
  }

  bindMapEvents(previewElem, imagesKeyUrl) {
    const addressElem = q('.post-detail-address-link', previewElem);
    if (addressElem) {
      // If swiper aren't created yet, interval usage
      // is good enough compared with complex async logic.
      let intervalId = null;

      on(addressElem, 'mouseenter', () => {
        let counter = 0;
        intervalId = setInterval(() => {
          counter++;
          const swiper = this.swipers[imagesKeyUrl];

          if (swiper) {
            swiper.slideTo(-1);
            swiper.autoplay.stop();
            clearInterval(intervalId);
          } else if (counter > 9) {
            clearInterval(intervalId);
          }
        }, 300);
      });

      on(addressElem, 'mouseleave', () => {
        if (intervalId) clearInterval(intervalId);

        const swiper = this.swipers[imagesKeyUrl];
        if (swiper) {
          swiper.autoplay.start();
        }
      });
    }
  }

  /**
   * Get images from cache or from request,
   * then run callback.
   *
   * @param url
   * @param callback
   */
  fetchImages(url, callback) {
    // This cache will be used only in current
    // method so initialize here.
    this.swiperImages = this.swiperImages || {};

    if (this.swiperImages[url]) {
      callback(this.swiperImages[url]);
    } else {
      this.swiperImages[url] = []; // it prevents ajax repetition
      ajax({
        url,
        success: (resp) => {
          this.swiperImages[url] = resp.map((r) => r.url);
          callback(this.swiperImages[url]);
        },
      });
    }
  }

  enableKey(e) {
    const target = e.delegateTarget;
    const key = target.getAttribute('data-key');
    const value = target.getAttribute('data-value');
    const keyInUrlPath = target.getAttribute('data-in-url');

    this.emit('enable', key, value, keyInUrlPath);

    e.preventDefault();

    this.scrollToFeed();
  }

  render(state) {
    this.renderPreloader(state);

    const feedFirstPage = document.querySelector('.feed.first-page');
    const feedOtherPage = document.querySelector('.feed.other-page');

    if (feedFirstPage && feedOtherPage) {
      if (state.page.index === 1) {
        feedFirstPage.style.display = 'unset';
        feedOtherPage.style.display = 'none';
      } else {
        feedFirstPage.style.display = 'none';
        feedOtherPage.style.display = 'unset';
      }
    }
    if (state.feed && !this.isSync(state)) {
      const likes = this.rememberLikes();
      this.renderFeed(state);
      this.restoreLikes(likes);
      this.initSwipers();
      this.makeLinks();
      emit(this.element, 'cascade-reflow', null, true);
      this.bindImagesReflow();
      this.renderSpecialWidgets();
      this.setPingIndices();
      this.AdButton.update();

      // Replacement for the block is not all on the object page. Not of great importance
      if (window.kudago && window.kudago.isItemPage) {
        const container = document.querySelector('.infiniteFeed-container');
        if (container) {
          const hederEls = container.querySelectorAll('h2');
          hederEls.forEach((item) => {
            const content = item.innerHTML;
            const h3 = document.createElement('h3');
            h3.classList.add(item.classList[0]);
            h3.innerHTML = content;
            item.replaceWith(h3);
          });
        }
      }
    }

    sendCountersView();
    this.loadImages();
  }

  loadImages() {
    echo.detach();
    echo.init({
      offset: 100,
      throttle: 250,
      unload: false,
      callback(elem, op) {
        elem.setAttribute('data-echo-loaded', op == 'load');
      },
    });
  }

  makeLinks() {
    const { makeLink } = window;
    if (makeLink) {
      q.all('[data-future-href]').forEach((i) => makeLink(i));
    }
  }

  rememberLikes() {
    // TODO: now it restores only mix likes
    // make it restore special widgets likes also
    // note that html of these items can be different while ids can be equal
    return q.all('.js-likes.post-fav', this.element).map((el) => ({
      id: el.getAttribute('data-id'),
      isActive: el.getAttribute('data-active'),
      html: el.innerHTML,
    }));
  }

  restoreLikes(liked) {
    liked.forEach(({ id, isActive, html }) => {
      const item = q(`.js-likes.post-fav[data-id="${id}"]`, this.element);
      if (item) {
        if (isActive) {
          item.setAttribute('data-active', true);
        } else {
          item.removeAttribute('data-active');
        }

        item.innerHTML = html;
      }
    });
  }

  isSync(state) {
    const { page } = state;
    return page.ajaxInProgress || page.loadMoreInProgress;
  }

  renderPreloader(state) {
    if (state.page.ajaxInProgress && !state.page.loadMoreInProgress) {
      this.element.appendChild(this.preloader);
    } else if (this.preloader.parentNode) {
      this.preloader.parentNode.removeChild(this.preloader);
    }
  }

  renderFeed(state) {
    const feed = this.getFeed(state);
    const relatedFeed = this.getRelatedFeed(state);
    const sorry = feed.sorry + relatedFeed.sorry;

    let sorryMarkup = '';
    if (sorry) {
      sorryMarkup = `<header class="sorry-header">${sorry}</header>`;
    }

    /*
     * TODO: this is good place for performance optimization if decrease server latency.
     */
    this.element.innerHTML = `
      ${feed.feed}
      ${sorryMarkup}
      <div class="feed--related">
        ${relatedFeed.feed}
      </div>
    `;
  }

  bindImagesReflow() {
    q.all('img', this.element).forEach((img) => {
      on(img, 'load', () => {
        emit('cascade-reflow');
        off(img, 'load');
      });
    });
  }

  renderSpecialWidgets() {
    q.all('.special-widget-wrapper').forEach((widget) => {
      // this need in case of filter switching
      if (!widget.children.length) {
        new SpecialWidget({
          el: widget,
        });
      } else {
        widget.classList.remove('preloading');
      }
    });
  }

  getFeed(state) {
    let feed = '';
    let sorry = '';

    if (state.feed && state.feed.length) {
      feed = state.feed.reduce((result, item) => result + item);
    } else {
      const nothingFoundText = _('Unfortunately, there is nothing suitable yet.');
      sorry = `<div>${nothingFoundText}</div>`;
    }

    return { feed, sorry };
  }

  getRelatedFeed(state) {
    let feed = '';
    let sorry = '';

    if (state.relatedFeed && state.relatedFeed.length) {
      feed = state.relatedFeed.reduce((result, item) => result + item);

      const mayBeInterestingText = _('You may also be interested in:');

      sorry = `<div>${mayBeInterestingText}</div>`;
    }

    return { feed, sorry };
  }
}

class CityController extends Controller {
  sync(data, state) {
    if (data.feed) {
      state.feed = data.feed;
    }

    if (data.related_feed) {
      state.relatedFeed = data.related_feed;
    }

    return state;
  }
}

/**
 * City feed is organized in three independent columns
 */
class CityLayout extends FeedLayout {
  constructor(...args) {
    super(...args);
    this.columns = q.all('.content-column', this.element);
    this.repositionLastItem();
  }

  clearContent() {
    q.all('.content-column', this.element)
      .forEach((column) => {
        const columnPosts = column.children;
        Array.from(columnPosts).forEach((post) => column.removeChild(post));
      });
  }

  renderFeed(state) {
    if (!state.page.hasMoreItemsToLoad) {
      this.element.classList.add('no-contraction');
      this.element.parentNode.classList.remove('feed-bottom-fade');
    }
    this.clearContent();
    state.feed.forEach((item) => this.appendItem(domify(item)));
  }

  /**
   * Place the last item in columns to the shortest column
   * We have to do this to remove empty place in the bottom of the city feed
   */
  repositionLastItem() {
    const maxHeightColumn = sortArrayByField(this.columns, 'clientHeight', 'desc')[0];
    const lastItem = maxHeightColumn.lastElementChild;

    if (!lastItem) return;

    lastItem.parentNode.removeChild(lastItem);
    this.appendItem(lastItem);
  }

  /**
   * Append item to the feed
   * The item is placed to the shortest column
   * @param  {Element} item - item to append
   */
  appendItem(item) {
    const minHeightColumn = sortArrayByField(this.columns, 'clientHeight')[0];
    minHeightColumn.appendChild(item);
  }
}

class SearchResultListController extends Controller {
  constructor(...args) {
    super(...args);

    const postElements = q.all('.post-list-item', this.view.element);
    const initialState = {
      feed: postElements.map((e) => e.innerHTML),
    };

    this.model.update((state) => ext(state, initialState));
  }

  sync(data, state, prevState) {
    const prevUrl = createUrl(prevState);
    const url = createUrl(state);

    if (state.feed && prevUrl == url) {
      state.feed = [].concat(state.feed, data.results);
    } else {
      state.feed = data.results;
    }

    return state;
  }
}

class SearchLayout extends FeedLayout {
  getFeed(state) {
    let feed = '';
    let sorry = '';

    if (state.feed && state.feed.length) {
      feed = state.feed.map((i) => {
        if (/^\s*<li>/.test(i)) {
          return i;
        }

        return `<li>${i}</li>`;
      }).join('');
    } else {
      let text;
      if (!state.search.q) {
        // empty search request
        text = _('Type what you want to find');
      } else {
        // nothing found
        text = _('Unfortunately, there is nothing suitable yet.');
      }

      sorry = `<div>${text}</div>`;
    }

    return { feed, sorry };
  }
}

module.exports = {
  FeedController,
  FeedLayout,
  CityController,
  CityLayout,
  SearchResultListController,
  SearchLayout,
};
